<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { reactive, ref, inject } from 'vue'
import photoIcon from '@/assets/photo.png'
import fillIcon from '@/assets/Fill 1@2x.png'
import deleteIcon from '@/assets/delete_white@2x.png'
import { updateProfileApi } from '@/api/admin'
import { usePopupAction } from '@/composables/usePopupAction'
import { uploadFile } from '@/api/UpLoad'

const props = defineProps<{
  currentAvatar?: string
  currentUsername?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', result: 'success' | 'error'): void
  (e: 'update-avatar', url: string): void
  (e: 'update-username', name: string): void
}>()

const formData = reactive({
  username: '',
})

const avatarPreview = ref<string | null>(null)

import { onMounted } from 'vue'

onMounted(() => {
  if (props.currentUsername) {
    formData.username = props.currentUsername
  }
  if (props.currentAvatar) {
    avatarPreview.value = props.currentAvatar
  }
})

const errors = reactive({
  username: '',
})

const selectedFile = ref<File | null>(null)

const { pending: saving, run: runSave } = usePopupAction()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
)!

function handleReset() {
  formData.username = props.currentUsername || ''
  errors.username = ''
  selectedFile.value = null
  avatarPreview.value = props.currentAvatar || null
  emit('close')
}


async function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    selectedFile.value = file
    console.log('handleFileChange file=', file)
    const reader = new FileReader()
    reader.onload = () => {
      avatarPreview.value = reader.result as string
    }
    reader.readAsDataURL(file)

    //上传图片接口 返回图片url
    const res = await uploadFile({ category: 'headImage', file: file })
    if (res.code === 200 && res.data) {
      avatarPreview.value = 'https://hm.back.escroud.com' + res.data.url
      emit('update-avatar', avatarPreview.value)
    }
  }
}

function handleDeleteAvatar() {
  selectedFile.value = null
  avatarPreview.value = null
}

async function handleSave() {
  if (saving.value) return
  const username = props.currentUsername || formData.username
  if (!username.trim()) {
    errors.username = 'Username is required'
    showToast('error', 'Username is required')
    return
  }

  // 乐观更新：先立刻更新前端，不等后端响应
  emit('update-username', username)
  if (selectedFile.value) {
    // 头像预览（base64）直接显示
    emit('update-avatar', avatarPreview.value || '')
  }

  await runSave(async () => {
    try {
      const response = await updateProfileApi({
        nickname: username,
        avatarUrl: avatarPreview.value || undefined,
      })

      if (response.code === 200 && response.data) {
        errors.username = ''
        emit('close')
        showToast('success', 'Successfully saved')
      } else {
        showToast('error', response.msg || 'Failed to update profile')
      }
    } catch {
      showToast('error', 'Failed to update profile')
    }
  })
}
</script>
<template>
  <div class="modal-overlay">
    <div class="panel profile-panel" @click.stop>
      <div class="panel-header">
        <h3>Profile</h3>
        <button class="close-btn" @click="$emit('close')">
          <img :src="fillIcon" alt="close" class="close-icon" />
        </button>
      </div>
      <div class="panel-body">
        <!-- Avatar 区域 -->
        <div class="form-group">
          <label class="form-label"><span class="required">*</span> Avatar</label>
          <label class="upload-box">
            <div v-if="avatarPreview" class="avatar-preview-wrapper">
              <img class="avatar-img" :src="avatarPreview" alt="Preview" />
              <div class="avatar-delete-overlay" @click.stop="handleDeleteAvatar">
                <img :src="deleteIcon" alt="delete" class="delete-icon" />
                <span class="delete-text">Delete</span>
              </div>
            </div>
            <template v-else>
              <img class="upload-icon" :src="photoIcon" alt="Upload" />
              <span class="upload-text">Upload</span>
            </template>
            <input type="file" hidden accept="image/*" @change="handleFileChange" />
          </label>
        </div>

        <!-- Username 区域 -->
        <div class="form-group">
          <label class="form-label" for="username">
            <span class="required">*</span> Username
          </label>
          <div class="input-wrapper username-fixed">
            <input
              id="username"
              :value="formData.username"
              type="text"
              class="form-input rounded-input rounded-input--fixed"
              readonly
              tabindex="-1"
            />
          </div>
        </div>

        <!-- 底部按钮区域 -->
        <div class="form-actions right-align">
          <div class="button-group">
            <button class="btn btn-cancel" @click="handleReset">Cancel</button>
            <button class="btn btn-submit" :disabled="saving" @click="handleSave">Submit</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 0;
}
/* 面板基础样式 */
.panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 530px;
  height: auto;
  min-height: 356px;
  max-width: none;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 0 24px;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #000000;
}

.panel-body {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
}

/* 关闭按钮 */
.close-btn {
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

/* 表单通用样式 */
.form-group {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #000000;
  padding-top: 16px;
  padding-bottom: 16px;
}
.required {
  color: #ff4d4f;
  margin-right: 4px;
}

/* Avatar 上传框样式 */
.upload-box {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  overflow: hidden;
  transition: all 0.2s ease;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-preview-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.avatar-delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.avatar-preview-wrapper:hover .avatar-delete-overlay {
  opacity: 1;
}

.delete-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.delete-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.upload-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
}

.upload-text {
  font-size: 0.75rem;
  color: #666;
}
/* Username 输入框样式 */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 482px;
}

.rounded-input {
  width: 100%;
  height: 32px;
  padding: 6px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #f5f6fb;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #cccccc;
  outline: none;
  box-sizing: border-box;
}

.rounded-input--fixed,
.rounded-input--fixed:focus {
  background: #f5f6fb;
  color: #cccccc;
  border: 1px solid #e0e0e0;
  box-shadow: none;
  cursor: default;
  pointer-events: none;
  user-select: none;
}

.username-fixed {
  cursor: default;
}

/* 底部按钮区域 */
.form-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 24px;
  padding-bottom: 24px;
}

.button-group {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

/* 按钮基础样式 */
.btn {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-cancel {
  border: 1px solid #000000;
  background: #fff;
  color: #000000;
}

.btn-cancel:hover {
  background: #000000;
  color: #ffffff;
}

.btn-submit {
  border: 1px solid #000;
  background: #000;
  color: #fff;
}

.btn-submit:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .panel {
    width: 95%;
    max-width: 530px;
    height: auto;
    max-height: 90vh;
  }

  .panel-body {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 10px;
  }

  .panel {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: calc(100vh - 20px);
    border-radius: 8px;
  }
}
</style>
